import { useSyncExternalStore } from 'react';

interface MediaQueryStore {
    subscribe: (cb: () => void) => () => void;
    getSnapshot: () => boolean;
    getServerSnapshot: () => boolean;
}

const stores = new Map<string, MediaQueryStore>();

/**
 * One lazily created `MediaQueryList` + listener set per query string, shared
 * by every subscriber, so hundreds of mounted components cost one OS-level
 * media query rather than one each. Server snapshot is always `false`.
 */
export function getMediaQueryStore(query: string): MediaQueryStore {
    let store = stores.get(query);
    if (store) return store;

    const listeners = new Set<() => void>();
    let currentValue = false;

    if (typeof window !== 'undefined') {
        const mql = window.matchMedia(query);
        currentValue = mql.matches;
        mql.addEventListener('change', (e) => {
            currentValue = e.matches;
            listeners.forEach((l) => l());
        });
    }

    store = {
        subscribe: (cb) => {
            listeners.add(cb);
            return () => {
                listeners.delete(cb);
            };
        },
        getSnapshot: () => currentValue,
        getServerSnapshot: () => false,
    };

    stores.set(query, store);
    return store;
}

/** SSR-safe, hydration-consistent `matchMedia` read; `false` on the server and first paint. */
export function useMediaQuery(query: string): boolean {
    const { subscribe, getSnapshot, getServerSnapshot } = getMediaQueryStore(query);
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Whether the primary pointer can hover (false on touch-only devices and during SSR). */
export function useCanHover(): boolean {
    return useMediaQuery('(hover: hover)');
}
